import { useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateCameraPlanColumn,
  useUpdateCameraPlanColumn,
  useDeleteCameraPlanColumn,
} from "@/hooks/useCameraPlans";
import type { CameraPlanColumn } from "@/types/camera-plans";

interface ManageColumnsDialogProps {
  cameraPlanId: string;
  columns: CameraPlanColumn[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageColumnsDialog({
  cameraPlanId,
  columns,
  open,
  onOpenChange,
}: ManageColumnsDialogProps) {
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"text" | "select" | "checkbox">(
    "text",
  );
  const [newOptions, setNewOptions] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editOptions, setEditOptions] = useState("");

  const createColumn = useCreateCameraPlanColumn();
  const updateColumn = useUpdateCameraPlanColumn();
  const deleteColumn = useDeleteCameraPlanColumn();

  const handleAdd = () => {
    if (!newName.trim()) return;

    const options =
      newType === "select"
        ? newOptions
            .split(",")
            .map((o) => o.trim())
            .filter(Boolean)
        : [];

    createColumn.mutate(
      {
        cameraPlanID: cameraPlanId,
        name: newName.trim(),
        columnType: newType,
        options,
        sortOrder: columns.length,
      },
      {
        onSuccess: () => {
          setNewName("");
          setNewType("text");
          setNewOptions("");
        },
      },
    );
  };

  const handleStartEdit = (col: CameraPlanColumn) => {
    setEditingId(col.id);
    setEditName(col.name);
    setEditOptions(col.columnType === "select" ? col.options.join(", ") : "");
  };

  const handleSaveEdit = (col: CameraPlanColumn) => {
    const updates: Record<string, unknown> = { name: editName.trim() };
    if (col.columnType === "select") {
      updates.options = editOptions
        .split(",")
        .map((o) => o.trim())
        .filter(Boolean);
    }
    updateColumn.mutate(
      { id: col.id, cameraPlanId, updates },
      { onSuccess: () => setEditingId(null) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Columns</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Existing columns */}
          {columns.length > 0 && (
            <div className="space-y-2">
              {columns.map((col) => (
                <div
                  key={col.id}
                  className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  {editingId === col.id ? (
                    <div className="flex-1 space-y-1">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-7 text-sm"
                        autoFocus
                      />
                      {col.columnType === "select" && (
                        <Input
                          value={editOptions}
                          onChange={(e) => setEditOptions(e.target.value)}
                          placeholder="Options (comma-separated)"
                          className="h-7 text-sm"
                        />
                      )}
                      <div className="flex gap-1">
                        <Button
                          offlineEdit
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => handleSaveEdit(col)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        data-offline-edit
                        className="flex-1 text-left text-sm hover:underline"
                        onClick={() => handleStartEdit(col)}
                      >
                        {col.name}
                      </button>
                      <Badge variant="outline" className="text-xs">
                        {col.columnType}
                      </Badge>
                      <Button
                        offlineEdit
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          deleteColumn.mutate({ id: col.id, cameraPlanId })
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add new column */}
          <div className="space-y-2 border-t pt-4">
            <Label className="text-sm font-medium">Add Column</Label>
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Column name"
                className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
              <Select
                value={newType}
                onValueChange={(v) =>
                  setNewType(v as "text" | "select" | "checkbox")
                }
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="select">Select</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newType === "select" && (
              <Input
                value={newOptions}
                onChange={(e) => setNewOptions(e.target.value)}
                placeholder="Options (comma-separated, e.g. Left, Right, Center)"
              />
            )}
            <Button
              offlineEdit
              size="sm"
              onClick={handleAdd}
              disabled={!newName.trim() || createColumn.isPending}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
